import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Loader2, Upload, Image as ImageIcon, X } from "lucide-react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { resolveAssetUrl } from "@/lib/utils";

const EMPTY = { name: "", category: "General", price: "", stock: "", description: "", image_url: "" };

export default function PartnerProductForm({ product, onSaved, disabled = false, disabledReason = "" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(product || EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setForm(product || EMPTY); }, [product]);

  const resolveImageUrl = (url) => {
    return resolveAssetUrl(url);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/partner/upload/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = resolveImageUrl(data?.url || "");
      setForm((prev) => ({ ...prev, image_url: fullUrl }));
      toast.success("Product image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingImage(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock) };
      if (product?.id) await api.put(`/partner/products/${product.id}`, payload);
      else await api.post("/partner/products", payload);
      toast.success(product?.id ? "Product updated (pending admin approval if changed)" : "Product submitted — awaiting Admin approval");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="bg-emerald-900 hover:bg-emerald-950 text-white rounded-full"
          data-testid={product?.id ? `edit-my-product-${product.id}` : "add-my-product"}
          disabled={!product?.id && disabled}
          title={!product?.id && disabled && disabledReason ? disabledReason : undefined}
        >
          {product?.id ? <Pencil className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />} {product?.id ? "Edit" : "Add to Gallery"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{product?.id ? "Edit Product" : "New Product"}</DialogTitle>
          <DialogDescription>
            {product?.id ? "Update partner product details and optionally replace the image." : "Create a new partner product for admin approval."}
          </DialogDescription>
        </DialogHeader>
        {product?.id ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="partner-edit-flow-note">
            আগে product info update করে <b>Update Product</b> দিন। পরে চাইলে আবার Edit খুলে image একটার পর একটা change করতে পারবেন।
          </div>
        ) : null}
        <form onSubmit={save} className="space-y-3" data-testid="partner-product-form">
          <div><Label>Product Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="my-prod-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category *</Label><Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" data-testid="my-prod-cat" /></div>
            <div><Label>Image URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1" placeholder="https://..." data-testid="my-prod-img" /></div>
          </div>
          <div>
            <Label>Product Image Upload</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={uploadImage}
                className="hidden"
                data-testid={product?.id ? "partner-edit-product-image-input" : "partner-add-product-image-input"}
              />
              <Button
                type="button"
                onClick={() => fileRef.current?.click()}
                variant="outline"
                disabled={uploadingImage}
                className="rounded-full"
                data-testid={product?.id ? "partner-edit-product-image-upload-button" : "partner-add-product-image-upload-button"}
              >
                {uploadingImage ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> {product?.id ? "Change Image" : "Upload Image"}</>
                )}
              </Button>
              {form.image_url ? (
                <div className="relative">
                  <img
                    src={form.image_url}
                    alt="Product preview"
                    className="w-16 h-16 rounded-lg border border-border object-cover"
                    data-testid={product?.id ? "partner-edit-product-image-preview" : "partner-add-product-image-preview"}
                  />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, image_url: "" }))}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                    data-testid={product?.id ? "partner-edit-product-image-remove" : "partner-add-product-image-remove"}
                    aria-label="Remove image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-lg bg-secondary/50 border border-dashed border-border flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-slate-400" />
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">JPG/PNG/WebP/GIF/SVG, max 5MB</p>
            {product?.id ? (
              <p className="text-[11px] text-emerald-700 mt-1">নতুন image upload করে Update Product দিলে পুরোনো image replace হবে।</p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Price (₹) *</Label><Input type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" data-testid="my-prod-price" /></div>
            <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1" data-testid="my-prod-stock" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="my-prod-desc" /></div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="my-prod-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (product?.id ? "Update Product" : "Save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

